/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import FullPageLoader from "../helper/FullPageLoader";
import { API_ROUTE, Routes } from "../helper/Routes";
import { getPortalMaintenanceMessage, shouldShowPortalMaintenance } from "../helper/portalMaintenance";
import { isApiUnreachable } from "../helper/apiAvailability";

export interface AuthPortalBranding {
  idBrand: string;
  brandName: string;
  frontColour: string;
  accentColour?: string;
  buttonColour?: string;
  topMenuBackground: string;
  topMenuFontColour: string;
  pageBackground: string;
  pageHeaderBackground?: string;
  pageHeaderLine?: string;
  pageBoxBorder: string;
  pageBoxFill: string;
  pageBoxFillAlt?: string;
  pageHeaderFontColour: string;
  pageStrapFontColour: string;
  textMutedColour?: string;
  folderSvg: number;
  footerBackground: string;
  footerGraphic?: string;
}

export interface AuthPartnerPortal {
  idPortal: string;
  idPortalStr: string;
  idPartner?: string | null;
  idPartnerStr?: string | null;
  partnerName: string;
  portalName: string;
  portalHost: string;
  logoFile: string;
  idCategorySet?: string | null;
  isActive: boolean;
  isPublic: boolean;
}

export interface AuthPortalContextResponse {
  resolutionStatus: string;
  requestedHost: string;
  isFallback: boolean;
  message: string;
  portalName: string;
  partner?: AuthPartnerPortal | null;
  brand?: AuthPortalBranding | null;
  maintenance?: {
    isEnabled: boolean;
    message: string;
    updatedAtUtc?: string | null;
  } | null;
}

interface AuthPortalContextValue {
  portal: AuthPortalContextResponse | null;
  brand: AuthPortalBranding | null;
  portalName: string;
  loading: boolean;
  error: string | null;
  maintenance: AuthPortalMaintenanceState | null;
}

const AuthPortalContext = createContext<AuthPortalContextValue | undefined>(undefined);

const PORTAL_CONTEXT_TIMEOUT_MS = 15000;

export interface AuthPortalMaintenanceState {
  title: string;
  message: string;
  source: "manual" | "api";
}

const applyBrandingStyles = (branding: AuthPortalBranding | null) => {
  const root = document.documentElement;

  if (!branding) {
    root.style.setProperty("--brand-font-colour", "#4F46E5"); // --fs-indigo
    root.style.setProperty("--brand-accent-colour", "#B97A5D"); // --fs-clay
    root.style.setProperty("--brand-button-colour", "#1A1410"); // --fs-ink
    root.style.setProperty("--brand-top-menu-bg", "#FAF6EE"); // --fs-cream
    root.style.setProperty("--brand-top-menu-font", "#1A1410"); // --fs-ink
    root.style.setProperty("--brand-page-bg", "#FAF6EE"); // --fs-cream
    root.style.setProperty("--brand-page-header-bg", "#FFFFFF"); // --fs-white
    root.style.setProperty("--brand-page-header-line", "#E5DDD0"); // --fs-line
    root.style.setProperty("--brand-page-box-border", "#D8CDBC"); // --fs-line-strong
    root.style.setProperty("--brand-page-box-fill", "#FFFFFF"); // --fs-white
    root.style.setProperty("--brand-page-box-fill-alt", "#F0E9DD"); // --fs-biscuit
    root.style.setProperty("--brand-page-header-font", "#1A1410"); // --fs-ink
    root.style.setProperty("--brand-page-strap-font", "#4A433D"); // --fs-ink-70
    root.style.setProperty("--brand-text-muted", "#6F665E"); // --fs-ink-50
    root.style.setProperty("--brand-folder-svg", "1");
    root.style.setProperty("--brand-footer-bg", "#F0E9DD"); // --fs-biscuit
    root.style.removeProperty("--brand-footer-graphic");
    return;
  }

  root.style.setProperty("--brand-font-colour", branding.frontColour);
  root.style.setProperty("--brand-accent-colour", branding.accentColour || branding.frontColour);
  root.style.setProperty("--brand-button-colour", branding.buttonColour || branding.frontColour);
  root.style.setProperty("--brand-top-menu-bg", branding.topMenuBackground);
  root.style.setProperty("--brand-top-menu-font", branding.topMenuFontColour);
  root.style.setProperty("--brand-page-bg", branding.pageBackground);
  root.style.setProperty(
    "--brand-page-header-bg",
    branding.pageHeaderBackground || branding.pageBoxFill || branding.pageBackground
  );
  root.style.setProperty(
    "--brand-page-header-line",
    branding.pageHeaderLine || branding.pageBoxBorder || "#E5DDD0"
  );
  root.style.setProperty("--brand-page-box-border", branding.pageBoxBorder);
  root.style.setProperty("--brand-page-box-fill", branding.pageBoxFill);
  root.style.setProperty("--brand-page-box-fill-alt", branding.pageBoxFillAlt || branding.pageBackground);
  root.style.setProperty("--brand-page-header-font", branding.pageHeaderFontColour);
  root.style.setProperty("--brand-page-strap-font", branding.pageStrapFontColour);
  root.style.setProperty("--brand-text-muted", branding.textMutedColour || branding.pageStrapFontColour);
  root.style.setProperty("--brand-folder-svg", branding.folderSvg.toString());
  root.style.setProperty("--brand-footer-bg", branding.footerBackground);

  if (branding.footerGraphic) {
    root.style.setProperty("--brand-footer-graphic", branding.footerGraphic);
  } else {
    root.style.removeProperty("--brand-footer-graphic");
  }
};

export const AuthPortalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [portal, setPortal] = useState<AuthPortalContextResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maintenance, setMaintenance] = useState<AuthPortalMaintenanceState | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadPortal = async () => {
      try {
        const response = await Axios.get<AuthPortalContextResponse>(`${API_ROUTE}${Routes.PORTAL_CONTEXT}`, {
          timeout: PORTAL_CONTEXT_TIMEOUT_MS,
        });
        if (!isMounted) {
          return;
        }

        const shouldEnterMaintenanceFromResponse = shouldShowPortalMaintenance(response.data);

        if (shouldEnterMaintenanceFromResponse) {
          setPortal(response.data);
          applyBrandingStyles(response.data.brand ?? null);
            setError(null);
            setMaintenance({
              title: "We’re working on FamilySafe",
              message: getPortalMaintenanceMessage(response.data),
              source: response.data.maintenance?.isEnabled ? "manual" : "api",
            });
          setLoading(false);
          return;
        }

        setPortal(response.data);
        applyBrandingStyles(response.data.brand ?? null);
        setError(null);
        setMaintenance(null);
      } catch (err) {
        console.error("Failed to load auth portal context", err);
        if (!isMounted) {
          return;
        }

        setPortal(null);
        applyBrandingStyles(null);

        const responseMessage =
          Axios.isAxiosError(err) && err.response?.data && typeof err.response.data === "object"
            ? (err.response.data as { message?: string }).message
            : undefined;
        const shouldEnterMaintenance = isApiUnreachable(err);

          if (shouldEnterMaintenance) {
            setError(null);
            setMaintenance({
              title: "We’re working on FamilySafe",
              message:
                responseMessage?.trim() ||
              "FamilySafe is currently unavailable while we carry out essential work on the platform.",
              source: "api",
            });
            return;
        }

        setMaintenance(null);
        setError("Unable to load portal details.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadPortal();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo<AuthPortalContextValue>(() => ({
    portal,
    brand: portal?.brand ?? null,
    portalName: portal?.portalName?.trim() || "FamilySafe",
    loading,
    error,
    maintenance,
  }), [portal, loading, error, maintenance]);

  return (
    <AuthPortalContext.Provider value={value}>
      {loading ? <FullPageLoader /> : children}
    </AuthPortalContext.Provider>
  );
};

export const useAuthPortal = () => {
  const context = useContext(AuthPortalContext);
  if (!context) {
    throw new Error("useAuthPortal must be used within an AuthPortalProvider");
  }

  return context;
};
